import React from 'react';
import { gql } from '@common/hooks';
import { Button, Row, Space } from 'antd';
import { ComplianceItemTables } from '@onboarding/components/compliance-item-tables/onboarding-item-tables';
import { ApproveAllItemsButton } from './_approve-all-items-button';
import { DownloadAllButton } from './_download-all-button';
import { ComplianceStateLegend } from '@onboarding/components';
import { Link } from '@common/components';
import { WorksListType, WorksListItemType } from '@onboarding/graphql';
import { buildDialogUrl } from '@common/services';

type Props = {
  worksList: WorksListType;
};

export function ComplianceItemApprovalList({ worksList }: Props) {
  const worksListItemById = (listItemId: string) => {
    if (!listItemId) return;
    const filteredItems = worksList.worksListItems.filter((item) => {
      if (item.id === listItemId) return item;
    });
    return filteredItems[0];
  };

  const listItemNotUploaded = (listItemId: string) => {
    const worksListItem = worksListItemById(listItemId);

    return (
      !worksListItem?.detailItem?.detailItemAttachments?.length ||
      worksListItem?.state === 'pending'
    );
  };

  const buildUpdateDrawerUrl = (complianceItemId: string) => {
    return buildDialogUrl('compliance-item-add-attachment-drawer', {
      props: { complianceItemId },
    });
  };

  const hasAnyAttachments = worksList.worksListItems.some(
    (item) =>
      item.detailItem?.detailItemAttachments &&
      item.detailItem.detailItemAttachments.length > 0
  );

  const actionColumns = [
    {
      title: 'Actions',
      key: 'actions',
      onHeaderCell: () => ({ style: { textAlign: 'right', width: '180px' } }),
      onCell: () => ({ style: { textAlign: 'right' } }),
      render: (worksListItem: WorksListItemType) => {
        const noUploadYet = listItemNotUploaded(worksListItem.id);

        const ButtonManageDocuments = () => (
          <Link
            to={buildDialogUrl('compliance-item-drawer', {
              props: { id: worksListItem.id },
            })}
          >
            <Button>Manage Documents</Button>
          </Link>
        );

        if (worksList.canUpdate.value && noUploadYet) {
          return (
            <Link to={buildUpdateDrawerUrl(worksListItem.id)}>
              <Button type="primary">Upload New</Button>
            </Link>
          );
        }

        if (worksList.canApproveReject.value && noUploadYet) {
          return (
            <span className="color-midnight-lighter text-heavy">
              Pending Upload
            </span>
          );
        }

        return <ButtonManageDocuments />;
      },
    },
  ];

  return (
    <section>
      <Row justify="space-between" align="middle">
        <ComplianceStateLegend />
        <Space>
          <DownloadAllButton
            worksListId={worksList.id}
            hasAttachments={hasAnyAttachments}
          />
          <ApproveAllItemsButton worksList={worksList} />
        </Space>
      </Row>
      <ComplianceItemTables
        worksList={worksList}
        actionColumns={actionColumns}
      />
    </section>
  );
}

ComplianceItemApprovalList.fragments = {
  ...ApproveAllItemsButton.fragments,
  worksList: gql`
    fragment WorksListWorksItemApprovalListInfo on WorksList {
      id
      completedItemsCount
      totalItemsCount
      ...OnboardingItemListWorksListInfo

      staffingRequestAssignment {
        id
        state
        worksProfile {
          id

          licenses {
            id
            state
            friendlyLicenseType
            number
            expiresOn
          }

          nurseCertifications {
            id
            certificationUuid
            name
            issuingBody
          }

          nurseAssessments {
            id
            completedOn

            assessment {
              id
              name
            }
          }

          nurseHealthDocs {
            id
            healthDoc {
              id
              name
              categories(filter: PROVIDER)
            }
            completedOn
            category
          }

          documents {
            id
            title
            docType
          }

          idDocs {
            id
            title
            docType
            docSubtype
          }
        }
      }
    }
    ${ComplianceItemTables.fragments.worksList}
  `,
};
